import { EmploymentField, useEmploymentSelection } from './EmploymentField'
import { useEffect, useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApp, type Localized } from '../../../i18n'
import { api } from '../../../api/client'
import { tone } from '../../../theme/tones'
import {
  Avatar, Button, FormInput, FormTextArea, Icon, IconButton, OptionSelect, PressCard,
} from '../../../ui'
import { pickFileWeb } from '../../../lib/files'
import { CONTRACT_ENUM_LABEL } from '../../../lib/edo-labels'
import type { Profile, CounterpartyCard } from '../../../lib/types'
import type {
  ContractCompany, ContractCounterpartyKind, ContractCounterpartyType, ContractCurrency, ContractDocType, ContractDuration,
  ContractPaymentType, ContractServiceKind, ContractSource,
} from '../../../lib/types'
import { useMe } from '../../users/useUsers'
import { useContractCountries, useContractCurrencies, useRoutePreview } from '../useEdo'
import {
  buildContractInput, contractFormMissing, contractFormValid, fixedContractRoute, needsParentContract, voenValid,
  type ContractFormState, type ContractMissing,
} from '../contractForm'
import { counterpartyLookupError } from '../counterpartyLookup'
import { CounterpartyBadges } from '../components/CardDetails'
import { Field, FormActions, MISSING_WORKPLACE, isWeb } from './shared'

/**
 * Согласование договора (ТЗ-2026-015). Форма типизирована, в отличие от произвольного документа,
 * но маршрут собирается так же — согласующие по порядку. Договор идёт строго последовательно,
 * поэтому переключателя «одновременно» здесь нет (прототип 05.08.2026).
 *
 * Обязательность полей — по §4.2 ТЗ (12 полей), самому строгому из трёх списков; клиент лишь
 * зеркалит бэк, источник правды — валидация сервера.
 */
/** Полный набор — фолбэк, когда ограничить нечем (компания без базы 1С либо зеркало молчит). */
const ALL_CURRENCIES = ['AZN', 'USD', 'EUR', 'RUB', 'GBP']

/** Подписи незаполненных полей для подсказки под кнопкой — те же, что у самих полей. */
const MISSING_LABEL: Record<ContractMissing, Localized> = {
  otherType: { ru: 'Уточнение типа', en: 'Type details' },
  contractNo: { ru: 'Номер основного договора', en: 'Master contract number' },
  appendix: { ru: 'Приложение к договору', en: 'Contract appendix', az: 'Müqaviləyə əlavə' },
  title: { ru: 'Наименование договора', en: 'Contract title' },
  voen: { ru: 'VÖEN контрагента', en: 'Counterparty VÖEN' },
  counterparty: { ru: 'Наименование контрагента', en: 'Counterparty name' },
  counterpartyType: { ru: 'Юр- или физлицо', en: 'Legal entity or individual', az: 'Hüquqi və ya fiziki şəxs' },
  country: { ru: 'Страна регистрации', en: 'Country of registration', az: 'Qeydiyyat ölkəsi' },
  address: { ru: 'Юридический адрес', en: 'Legal address', az: 'Hüquqi ünvan' },
  amount: { ru: 'Сумма (больше нуля)', en: 'Amount (above zero)', az: 'Məbləğ (sıfırdan çox)' },
  subject: { ru: 'Предмет договора', en: 'Subject' },
  approvers: { ru: 'Маршрут согласования', en: 'Approval route' },
  files: { ru: 'Проект договора', en: 'Draft contract' },
}

export function ContractForm({ onDone }: { onDone: () => void }) {
  const employment = useEmploymentSelection()
  const { t } = useApp()
  const qc = useQueryClient()
  const me = useMe()
  const [f, setF] = useState({
    title: '', voen: '', counterparty: '', subject: '',
    amount: '', vatTerms: '', otherType: '', contractNo: '', docNo: '', comment: '', address: '',
  })
  const set = (k: keyof typeof f) => (v: string) => setF((prev) => ({ ...prev, [k]: v }))
  const [company, setCompany] = useState<ContractCompany>('AS_QRUP')
  const [source, setSource] = useState<ContractSource>('internal_template')
  const [docType, setDocType] = useState<ContractDocType>('contract')
  const [serviceKind, setServiceKind] = useState<ContractServiceKind>('services')
  const [hasAdditionalAppendix, setHasAdditionalAppendix] = useState<boolean | null>(null)
  const [duration, setDuration] = useState<ContractDuration>('1y')
  const [currency, setCurrency] = useState<ContractCurrency>('AZN')
  const [paymentType, setPaymentType] = useState<ContractPaymentType>('prepaid')
  const [counterpartyKind, setCounterpartyKind] = useState<ContractCounterpartyKind>('local')
  const [counterpartyType, setCounterpartyType] = useState<ContractCounterpartyType | null>(null)
  const [countryId, setCountryId] = useState('')
  // Обязательный маршрут зависит от компании и приходит с сервера: в форме он только читается,
  // а дополнительные люди вставляются перед отмеченным финальным согласующим.
  const routePreview = useRoutePreview(
    'CONTRACT',
    undefined,
    undefined,
    employment.employmentId,
    employment.ready,
    company,
  )
  const fixedRoute = fixedContractRoute(routePreview.data)
  /**
   * Валюты, которые примет база выбранной компании. Набор разный в каждой базе 1С (у AZMAN нет
   * евро, фунт есть только у AS İnşaat), и валюта не из справочника роняет проводку уже ПОСЛЕ
   * полного круга согласования — заявка LGL-2026-000099. Пустой ответ = «ограничивать нечем»
   * (компания без базы либо зеркало молчит): показываем полный список, а не пустой.
   */
  const allowedCur = useContractCurrencies(company).data?.currencies
  const curOptions = allowedCur?.length ? allowedCur : ALL_CURRENCIES
  // Смена компании могла оставить валюту, которой в её базе нет — переключаем на первую
  // доступную. Молча: пользователь не выбирал невозможное, это следствие смены компании.
  useEffect(() => {
    if (curOptions.length && !curOptions.includes(currency)) {
      setCurrency(curOptions[0] as ContractCurrency)
    }
  }, [curOptions, currency])
  /**
   * Страны справочника 1С базы компании — для зарубежного контрагента (19.10.2026). Справочники у
   * баз маленькие и разные, поэтому выбор только из своего: чужой GUID 1С отбивает. `required` —
   * договор компании проводится в 1С, и без реквизитов 1С не создаст контрагента.
   */
  const countryOptions = useContractCountries(company).data
  const requisitesRequired = !!countryOptions?.required
  const countries = countryOptions?.countries ?? []
  // Смена компании меняет справочник: GUID страны свой в каждой базе — выбранную сбрасываем.
  useEffect(() => {
    if (countryId && !countries.some((c) => c.id === countryId)) setCountryId('')
  }, [countries, countryId])
  const [route, setRoute] = useState<PickedPerson[]>([])
  const [files, setFiles] = useState<File[]>([])
  // Сборка тела запроса и правило готовности — в contractForm.ts (тестируются без рендера).
  const state: ContractFormState = {
    ...f,
    company, source, docType, serviceKind, hasAdditionalAppendix, duration, currency, paymentType, counterpartyKind,
    requisitesRequired, counterpartyType, countryId,
    approvers: route.map((p) => p.id),
  }

  // Фолбэк на сам код: список валют приходит из справочника 1С и может содержать значения без
  // подписи (у AS İnşaat, например, рядом с `RUB` лежит вторая запись рубля `RUR`). Без фолбэка
  // такая опция рендерилась бы пустой строкой — выбрать её можно, а что выбрал, не видно.
  const opts = (codes: string[]) =>
    codes.map((value) => ({ value, label: t(CONTRACT_ENUM_LABEL[value] ?? { ru: value, en: value }) }))

  const create = useMutation({
    mutationFn: async () => {
      const uploadIds: string[] = []
      for (const file of files) {
        const form = new FormData()
        form.append('file', file as unknown as Blob)
        uploadIds.push((await api.uploadEdoDoc(form)).id)
      }
      return api.createContract({ ...buildContractInput(state, uploadIds), employmentId: employment.employmentId })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      onDone()
    },
  })

  const local = counterpartyKind === 'local'
  const voenOk = voenValid(f.voen)
  // Проверка контрагента в e-taxes (§7): подсказка, а не гейт — не нашли или госпортал лёг,
  // заявка всё равно подаётся с ручными реквизитами.
  const [checked, setChecked] = useState<CounterpartyCard | null>(null)
  const [checkError, setCheckError] = useState<{ ru: string; en: string } | null>(null)
  const check = useMutation({
    // Повторное нажатие — с refresh: пересверяем риск-статус (§7.3), а не отдаём кэш суток.
    mutationFn: () => api.lookupCounterparty(f.voen, checked !== null),
    onSuccess: (c) => {
      setChecked(c); setCheckError(null)
      set('counterparty')(c.name) // наименование — единственное поле формы, которое отдаёт реестр
    },
    onError: (e) => { setChecked(null); setCheckError(counterpartyLookupError(e)) },
  })
  const ok = employment.ready && contractFormValid(state, files.length) && !create.isPending
  const missing = [
    ...(employment.ready ? [] : [t(MISSING_WORKPLACE)]),
    ...contractFormMissing(state, files.length).map((k) => t(MISSING_LABEL[k])),
  ]
  const submit = () => ok && create.mutate()

  return (
    <YStack gap={14}>
      <EmploymentField selection={employment} />
      {/* Порядок полей — по правке Теймура 18.09.2026 (поверх правок юристов от того же дня):
          тип документа → приложение → тип контракта → наименование → номер → длительность → компания
          → источник → блок контрагента → сумма с валютой → вид оплаты → НДС → предмет; дальше как было. */}
      <Field label={t({ ru: 'Тип документа', en: 'Document type' })} required>
        <OptionSelect options={opts(['contract', 'addendum', 'annex', 'other'])} value={[docType]}
          onChange={(next) => setDocType(next[0] as ContractDocType)} />
      </Field>

      {/* Условные поля (§4.3 + правки прототипа 24.08.2026). Набранное значение при смене типа
          остаётся в стейте — вернувшийся пользователь увидит его снова, — но в тело запроса не
          попадает: buildContractInput шлёт эти поля только там, где они уместны, а лишнее бэк
          отбивает 400. */}
      {docType === 'other' && (
        <Field label={t({ ru: 'Уточнение типа', en: 'Type details' })} required>
          <FormInput value={f.otherType} onChangeText={set('otherType')} maxLength={200}
            placeholder={t({ ru: 'Например: соглашение о неразглашении', en: 'e.g. non-disclosure agreement' })} />
        </Field>
      )}

      {needsParentContract(docType) && (
        <Field label={t({ ru: 'Номер основного договора', en: 'Master contract number' })} required>
          <YStack gap={6}>
            <FormInput value={f.contractNo} onChangeText={set('contractNo')} maxLength={100}
              placeholder="LGL-2026-000041" />
            <Text fontSize={12} color="$muted">
              {t({ ru: 'Договор, который дополняется этим документом.',
                   en: 'The contract this document amends.' })}
            </Text>
          </YStack>
        </Field>
      )}

      <Field label={t({
        ru: 'Приложение к договору',
        en: 'Contract appendix',
        az: 'Müqaviləyə əlavə',
      })} required>
        <OptionSelect
          options={[
            { value: 'true', label: t(CONTRACT_ENUM_LABEL.true) },
            { value: 'false', label: t(CONTRACT_ENUM_LABEL.false) },
          ]}
          value={hasAdditionalAppendix === null ? [] : [String(hasAdditionalAppendix)]}
          onChange={(next) => setHasAdditionalAppendix(next[0] === 'true')}
          placeholder={t({ ru: 'Выберите вариант', en: 'Select an option', az: 'Variantı seçin' })}
          list
        />
      </Field>

      <Field label={t({ ru: 'Тип контракта', en: 'Contract type' })} required>
        <OptionSelect options={opts(['services', 'goods', 'works', 'rent'])} value={[serviceKind]}
          onChange={(next) => setServiceKind(next[0] as ContractServiceKind)} />
      </Field>

      <Field label={t({ ru: 'Наименование договора', en: 'Contract title' })} required>
        <FormInput value={f.title} onChangeText={set('title')} maxLength={200}
          placeholder={t({ ru: 'Например: договор поставки оборудования', en: 'e.g. equipment supply contract' })} />
      </Field>

      <Field label={t({ ru: 'Номер договора', en: 'Contract number' })}>
        <YStack gap={6}>
          <FormInput value={f.docNo} onChangeText={set('docNo')} maxLength={100}
            placeholder="DG-2026-0018" />
          <Text fontSize={12} color="$muted">
            {t({ ru: 'Регистрационный номер присваивают юристы — можно оставить пустым и заполнить позже.',
                 en: 'The registration number is assigned by Legal — you may leave it empty and fill it in later.' })}
          </Text>
        </YStack>
      </Field>

      <Field label={t({ ru: 'Длительность', en: 'Duration' })} required>
        <YStack gap={6}>
          <OptionSelect options={opts(['1y', '2y', '3y', '5y', 'perpetual'])} value={[duration]}
            onChange={(next) => setDuration(next[0] as ContractDuration)} />
          <Text fontSize={12} color="$muted">
            {t({ ru: 'Точные даты действия вы укажете сами вместе со сканом подписанного экземпляра — после того как договор согласуют все участники.',
                 en: 'You will enter the exact validity dates yourself together with the scan of the signed copy — after all approvers have approved the contract.',
                 az: 'Dəqiq qüvvədə olma tarixlərini müqavilə bütün iştirakçılar tərəfindən razılaşdırıldıqdan sonra imzalanmış nüsxənin skanı ilə birlikdə özünüz göstərəcəksiniz.' })}
          </Text>
        </YStack>
      </Field>

      <Field label={t({ ru: 'Компания-подписант', en: 'Signing company' })} required>
        <YStack gap={6}>
          <OptionSelect options={opts(['AS_QRUP', 'AZMAN', 'AS_INSHAAT', 'AS_DEVELOPMENT', 'OTHER'])} value={[company]}
            onChange={(next) => setCompany(next[0] as ContractCompany)} />
          <Text fontSize={12} color="$muted">
            {t({ ru: 'От компании зависит маршрут согласования. После подачи её изменить нельзя.',
                 en: 'The company defines the approval route and cannot be changed after submission.' })}
          </Text>
        </YStack>
      </Field>

      <Field label={t({ ru: 'Источник документа', en: 'Document source' })} required>
        <OptionSelect options={opts(['internal_template', 'counterparty_template'])} value={[source]}
          onChange={(next) => setSource(next[0] as ContractSource)} />
      </Field>

      <Field label={t({ ru: 'Тип контрагента', en: 'Counterparty type' })} required>
        <YStack gap={6}>
          <OptionSelect options={opts(['local', 'foreign'])} value={[counterpartyKind]}
            onChange={(next) => {
              // Смена типа обнуляет проверку: реестр Госналоговой отвечает только про местных.
              setChecked(null); setCheckError(null)
              setCounterpartyKind(next[0] as ContractCounterpartyKind)
            }} />
          <Text fontSize={12} color="$muted">
            {t({ ru: 'Зарубежный контрагент — нерезидент Азербайджана: азербайджанского VÖEN у него может не быть, и в реестре Госналоговой его не проверить.',
                 en: 'A foreign counterparty is a non-resident: it may have no Azerbaijani VÖEN at all and cannot be checked in the state tax registry.' })}
          </Text>
        </YStack>
      </Field>

      <Field label={local
        ? t({ ru: 'VÖEN контрагента', en: 'Counterparty VÖEN' })
        : t({ ru: 'Налоговый или регистрационный номер', en: 'Tax or registration number' })} required={local}>
        <YStack gap={6}>
          <XStack gap={8} alignItems="center">
            <YStack flex={1}>
              <FormInput value={f.voen} onChangeText={(v: string) => {
                // Правка ВÖİN обнуляет проверку: иначе карточка прошлого контрагента
                // осталась бы висеть под новым номером.
                setChecked(null); setCheckError(null)
                set('voen')(local ? v.replace(/\D/g, '').slice(0, 10) : v.slice(0, 50))
              }} keyboardType={local ? 'number-pad' : 'default'}
                placeholder={local ? '1234567890' : t({ ru: 'Например, GB123456789 (можно не заполнять)', en: 'e.g. GB123456789 (optional)' })} />
            </YStack>
            {local && (
              <Button variant="default" disabled={!voenOk || check.isPending}
                opacity={voenOk && !check.isPending ? 1 : 0.5}
                cursor={voenOk && !check.isPending ? 'pointer' : 'default'}
                icon={<Icon name="search" size={15} color="$muted" />}
                onPress={() => voenOk && !check.isPending && check.mutate()}>
                {check.isPending
                  ? t({ ru: 'Проверка…', en: 'Checking…' })
                  : checked
                    ? t({ ru: 'Проверить заново', en: 'Re-check' })
                    : t({ ru: 'Проверить', en: 'Check' })}
              </Button>
            )}
          </XStack>
          {local && f.voen !== '' && !voenOk && (
            <Text fontSize={12} color={tone('red').text}>
              {t({ ru: 'VÖEN — ровно 10 цифр', en: 'VÖEN must be exactly 10 digits' })}
            </Text>
          )}
          {checkError && <Text fontSize={12} color="$muted">{t(checkError)}</Text>}
          {checked && (
            <YStack gap={4} padding={9} borderRadius={7} backgroundColor="$bgLayout"
              borderWidth={1} borderColor="$borderColor">
              <CounterpartyBadges card={checked} />
              {/* Адрес и представителя форма не спрашивает, но юрист вносит их в текст договора —
                  показываем, чтобы не искать заново в реестре. */}
              {checked.legalAddress && (
                <Text fontSize={12} color="$muted">
                  {t({ ru: 'Юр. адрес', en: 'Legal address' })}: {checked.legalAddress}
                </Text>
              )}
              {checked.legitimate && (
                <Text fontSize={12} color="$muted">
                  {t({ ru: 'Законный представитель', en: 'Legal representative' })}: {checked.legitimate}
                </Text>
              )}
            </YStack>
          )}
        </YStack>
      </Field>

      {/* Необязательно (§7): проверка по ВÖİN сама подтягивает наименование из реестра. У
          зарубежного реестра нет — там имя единственное, чем контрагент назван, и обязательно. */}
      <Field label={t({ ru: 'Наименование контрагента', en: 'Counterparty name' })} required={!local}>
        <FormInput value={f.counterparty} onChangeText={set('counterparty')} maxLength={300}
          placeholder={t({ ru: 'Как в учредительных документах', en: 'As in the registration documents' })} />
      </Field>

      {/* Реквизиты зарубежного контрагента (19.10.2026). Реестра e-taxes у него нет, а контрагента,
          которого нет в базе 1С, портал создаёт сам — без адреса, страны и вида лица 1С его не
          примет, и договор упадёт уже после полного круга (LGL-2026-000115, 000155). Только для
          компаний, чьи договоры проводятся в 1С. */}
      {!local && requisitesRequired && (
        <>
          <Field label={t({ ru: 'Юр- или физлицо', en: 'Legal entity or individual', az: 'Hüquqi və ya fiziki şəxs' })} required>
            <OptionSelect options={opts(['legalEntity', 'naturalPerson'])} value={counterpartyType ? [counterpartyType] : []}
              onChange={(next) => setCounterpartyType((next[0] as ContractCounterpartyType) ?? null)} />
          </Field>
          <Field label={t({ ru: 'Страна регистрации', en: 'Country of registration', az: 'Qeydiyyat ölkəsi' })} required>
            <YStack gap={6}>
              <OptionSelect list searchable options={countries.map((c) => ({ value: c.id, label: c.name }))}
                value={countryId ? [countryId] : []} onChange={(next) => setCountryId(next[0] ?? '')} />
              <Text fontSize={12} color="$muted">
                {countries.length
                  ? t({ ru: 'Список — из справочника 1С выбранной компании. Нужной страны нет — попросите бухгалтерию добавить её в 1С.',
                       en: "The list comes from the selected company's 1C catalog. If the country is missing, ask accounting to add it in 1C.",
                       az: 'Siyahı seçilmiş şirkətin 1C kataloqundandır. Lazımi ölkə yoxdursa, mühasibatlıqdan onu 1C-yə əlavə etməyi xahiş edin.' })
                  : t({ ru: 'Справочник стран 1С сейчас недоступен — попробуйте позже.',
                       en: 'The 1C country catalog is unavailable right now — try again later.',
                       az: '1C ölkə kataloqu hazırda əlçatan deyil — sonra yenidən cəhd edin.' })}
              </Text>
            </YStack>
          </Field>
          <Field label={t({ ru: 'Юридический адрес', en: 'Legal address', az: 'Hüquqi ünvan' })} required>
            <FormInput value={f.address} onChangeText={set('address')} maxLength={500}
              placeholder={t({ ru: 'Как в регистрационных документах', en: 'As in the registration documents', az: 'Qeydiyyat sənədlərində olduğu kimi' })} />
          </Field>
        </>
      )}

      <XStack gap={10}>
        <YStack flex={1}>
          <Field label={t({ ru: 'Сумма', en: 'Amount' })}>
            <FormInput value={f.amount} onChangeText={(v: string) => set('amount')(v.replace(/[^\d.]/g, ''))}
              keyboardType="decimal-pad" placeholder="0.00" />
          </Field>
        </YStack>
        <YStack flex={1}>
          <Field label={t({ ru: 'Валюта', en: 'Currency' })} required>
            {/* list: валют пять и список будет расти — чипы в строку уже не помещаются */}
            <OptionSelect list options={opts(curOptions)} value={[currency]}
              onChange={(next) => setCurrency(next[0] as ContractCurrency)} />
          </Field>
        </YStack>
      </XStack>

      <Field label={t({ ru: 'Вид оплаты', en: 'Payment type' })} required>
        {/* list: видов оплаты будет больше — сразу выпадающий список, а не чипы */}
        <OptionSelect list options={opts(['prepaid', 'on_fact', 'staged', 'prepaid_staged'])} value={[paymentType]}
          onChange={(next) => setPaymentType(next[0] as ContractPaymentType)} />
      </Field>

      <Field label={t({ ru: 'Условия НДС', en: 'VAT terms', az: 'ƏDV şərtləri' })}>
        <FormInput
          value={f.vatTerms}
          onChangeText={set('vatTerms')}
          maxLength={300}
          placeholder={t({
            ru: 'Например: НДС включён в стоимость',
            en: 'e.g. VAT is included in the price',
            az: 'Məsələn: ƏDV qiymətə daxildir',
          })}
        />
      </Field>

      <Field label={t({ ru: 'Предмет договора', en: 'Subject' })} required>
        <FormTextArea value={f.subject} onChangeText={set('subject')} maxLength={10000}
          placeholder={t({ ru: 'Что поставляется или оказывается по договору', en: 'What is supplied or provided' })}
          minHeight={110} />
      </Field>

      {/* Срок действия здесь не собирается: точные даты появляются только вместе с подписанным
          экземпляром, поэтому их вносит автор на шаге «Подписанный экземпляр» после согласования
          всеми участниками (решение бизнеса 27.08.2026, перенос на автора — 23.09.2026). На время
          маршрута срок описывает «Длительность» выше. */}

      <Field label={t({ ru: 'Маршрут согласования', en: 'Approval route' })} required>
        {company !== 'OTHER' && fixedRoute.required.map((person, index) => (
          <XStack key={person.id} alignItems="center" gap={10} marginBottom={8}>
            <Text fontSize={12} fontWeight="700" color="$primary" width={18}>{`${index + 1}.`}</Text>
            <Avatar name={person.fullName} uri={undefined} size={28} />
            <YStack flex={1}>
              <Text fontSize={13} color="$color">{person.fullName}</Text>
              {!!person.jobTitle && <Text fontSize={11} color="$muted">{person.jobTitle}</Text>}
            </YStack>
            <Icon name="lock" size={14} color="$muted" />
          </XStack>
        ))}
        <PeoplePicker
          chosen={route}
          onChange={setRoute}
          exclude={[
            me.data?.id,
            ...fixedRoute.required.map((person) => person.id),
            fixedRoute.final?.id,
          ]}
          numbered
          startIndex={fixedRoute.required.length}
          empty={company === 'OTHER'
            ? t({ ru: 'Добавьте согласующих — они получат документ по очереди.',
                  en: 'Add approvers — they receive the document one by one.' })
            : t({ ru: 'Дополнительные согласующие не выбраны.',
                  en: 'No additional approvers selected.' })}
        />
        {company !== 'OTHER' && !!fixedRoute.final && (
          <XStack alignItems="center" gap={10} marginTop={10}>
            <Text fontSize={12} fontWeight="700" color="$primary" width={18}>
              {`${fixedRoute.required.length + route.length + 1}.`}
            </Text>
            <Avatar name={fixedRoute.final.fullName} uri={undefined} size={28} />
            <YStack flex={1}>
              <Text fontSize={13} color="$color">{fixedRoute.final.fullName}</Text>
              <Text fontSize={11} color="$muted">
                {fixedRoute.final.jobTitle || t({ ru: 'Финальное согласование', en: 'Final approval' })}
              </Text>
            </YStack>
            <Icon name="lock" size={14} color="$muted" />
          </XStack>
        )}
        {company !== 'OTHER' && (
          <Text fontSize={11} color="$muted" marginTop={8}>
            {t({
              ru: 'Шаги с замком обязательны. Дополнительные согласующие проходят после шага 2 и до финального.',
              en: 'Locked steps are mandatory. Additional approvers review after step 2 and before the final approver.',
            })}
          </Text>
        )}
      </Field>

      <Field label={t({ ru: 'Проект договора', en: 'Draft contract' })} required>
        {isWeb ? (
          <YStack gap={8}>
            {files.map((file) => (
              <XStack key={file.name} alignItems="center" gap={8}>
                <Icon name="paperclip" size={14} color="$muted" />
                <Text fontSize={12} color="$color" flex={1}>{file.name}</Text>
                <IconButton onPress={() => setFiles(files.filter((x) => x !== file))}>
                  <Icon name="x" size={14} color="$muted" />
                </IconButton>
              </XStack>
            ))}
            <Button variant="default" block icon={<Icon name="paperclip" size={15} color="$muted" />}
              onPress={async () => {
                const picked = await pickFileWeb('.pdf,.doc,.docx,.jpg,.jpeg,.png')
                if (picked) setFiles((prev) => [...prev, picked])
              }}>
              {t({ ru: 'Прикрепить проект договора', en: 'Attach the draft' })}
            </Button>
            <Text fontSize={12} color="$muted">
              {t({ ru: 'Скан подписанного экземпляра прикладываете вы — на шаге «Подписанный экземпляр» после согласования всеми участниками.',
                   en: 'You attach the scan of the signed copy yourself — on the "Signed copy" step after all approvals.',
                   az: 'İmzalanmış nüsxənin skanını siz əlavə edirsiniz — bütün razılaşdırmalardan sonra «İmzalanmış nüsxə» mərhələsində.' })}
            </Text>
          </YStack>
        ) : (
          <Text fontSize={12} color="$muted">
            {t({
              ru: 'Проект договора обязателен, а выбор файла есть только в веб-версии — подайте заявку с компьютера.',
              en: 'The draft is mandatory and file picking works only in the web version — submit from a desktop browser.',
            })}
          </Text>
        )}
      </Field>

      <Field label={t({ ru: 'Комментарий', en: 'Comment' })}>
        <FormInput value={f.comment} onChangeText={set('comment')} maxLength={1000}
          placeholder={t({ ru: 'Необязательно', en: 'Optional' })} />
      </Field>

      <FormActions onCancel={onDone} onSubmit={submit} ok={ok} m={create} missing={missing} />
    </YStack>
  )
}

/** Выбранный в пикере человек: из поиска — полный профиль, из справочников процесса — только id и ФИО. */
export type PickedPerson = Pick<Profile, 'id' | 'fullName' | 'jobTitle' | 'avatarUrl'>

/**
 * Пикер людей для маршрута и информируемых: поиск от 2 символов, выбранные — списком с удалением.
 * `exclude` убирает из выдачи себя и тех, кто уже выбран в соседнем списке: один человек не может
 * быть одновременно согласующим и информируемым (бэк ответит 400). Общий с получателями IT-работ.
 */
export function PeoplePicker({
  chosen, onChange, exclude, empty, numbered, startIndex = 0,
}: {
  chosen: PickedPerson[]
  onChange: (next: PickedPerson[]) => void
  exclude: (string | undefined)[]
  empty: string
  numbered?: boolean
  /** Сколько обязательных шагов маршрута идёт ДО пикера — чтобы нумерация не начиналась заново. */
  startIndex?: number
}) {
  const { t } = useApp()
  const [search, setSearch] = useState('')
  const q = search.trim()
  const people = useQuery({
    // Договор межкомпанийный: юрист ищет согласующих по всему холдингу (сервер сужает не-юристов сам).
    queryKey: ['users', q, 'all'],
    queryFn: () => api.users(q, { scope: 'all' }),
    enabled: q.length >= 2,
    staleTime: 60_000,
  })
  const taken = new Set([...chosen.map((p) => p.id), ...exclude.filter(Boolean) as string[]])
  const found = (people.data ?? []).filter((p) => !taken.has(p.id)).slice(0, 8)

  return (
    <YStack gap={10}>
      {chosen.length === 0 ? (
        <Text fontSize={12} color="$muted">{empty}</Text>
      ) : (
        chosen.map((p, i) => (
          <XStack key={p.id} alignItems="center" gap={10}>
            {numbered && (
              <Text fontSize={12} fontWeight="700" color="$primary" width={18}>{`${i + 1 + startIndex}.`}</Text>
            )}
            <Avatar name={p.fullName} uri={p.avatarUrl} size={28} />
            <YStack flex={1}>
              <Text fontSize={13} color="$color">{p.fullName}</Text>
              {!!p.jobTitle && <Text fontSize={11} color="$muted">{p.jobTitle}</Text>}
            </YStack>
            <IconButton onPress={() => onChange(chosen.filter((x) => x.id !== p.id))}>
              <Icon name="x" size={15} color="$muted" />
            </IconButton>
          </XStack>
        ))
      )}
      <FormInput value={search} onChangeText={setSearch}
        placeholder={t({ ru: 'Поиск сотрудника (от 2 символов)', en: 'Search employee (2+ chars)' })} />
      {found.map((p) => (
        <PressCard key={p.id} padding={9} onPress={() => { onChange([...chosen, p]); setSearch('') }}>
          <XStack alignItems="center" gap={10}>
            <Avatar name={p.fullName} uri={p.avatarUrl} size={28} />
            <YStack flex={1}>
              <Text fontSize={13} color="$color">{p.fullName}</Text>
              {!!p.jobTitle && <Text fontSize={11} color="$muted">{p.jobTitle}</Text>}
            </YStack>
            <Icon name="plus" size={15} color="$primary" />
          </XStack>
        </PressCard>
      ))}
    </YStack>
  )
}
